import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminFetch, fmtRelative } from './api';
import { PageHeader, EmptyState, ErrorBlock, StatusPill, ScrollTable } from './widgets';

const TABS = [
  { value: 'all',          label: 'All' },
  { value: 'eligible',     label: 'Eligible' },
  { value: 'sent',         label: 'Sent' },
  { value: 'replied',      label: 'Replied' },
  { value: 'bounced',      label: 'Bounced' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
];

const PAGE = 50;

export default function AdminProspects() {
  const [status, setStatus] = useState('all');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { document.title = 'Sudco Admin — Prospects'; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await adminFetch(`/api/admin-stats/prospects?status=${status}&limit=${PAGE}&offset=${offset}`);
      setData(d);
    } catch (err) {
      setError(err);
    }
  }, [status, offset]);

  useEffect(() => { load(); }, [load]);

  const pickTab = (t) => { setOffset(0); setStatus(t); };

  const totalPages = useMemo(() => data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1, [data]);
  const page = Math.floor(offset / PAGE) + 1;

  return (
    <>
      <PageHeader title="Prospects" subtitle="Filter by pipeline state." />

      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => pickTab(t.value)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              status === t.value
                ? 'bg-gradient-brand text-white border-transparent'
                : 'bg-surface text-text-muted border-border hover:text-text hover:bg-surface-2'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <ErrorBlock error={error} onRetry={load} />}

      {data && data.prospects.length === 0 && (
        <EmptyState title="No prospects in this view" />
      )}

      {data && data.prospects.length > 0 && (
        <ScrollTable>
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-text-muted text-xs uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Business</th>
                <th className="text-left px-4 py-2.5 font-medium">Industry</th>
                <th className="text-left px-4 py-2.5 font-medium">Email</th>
                <th className="text-left px-4 py-2.5 font-medium">Stage</th>
                <th className="text-left px-4 py-2.5 font-medium">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.prospects.map((p) => {
                const stage = p.cold_replied_at ? 'replied'
                  : p.bounced_at ? 'bounced'
                  : p.unsubscribed_at ? 'unsubscribed'
                  : p.cold_followup_at ? 'followup'
                  : p.cold_outreach_at ? 'sent'
                  : 'eligible';
                const lastTs = p.cold_replied_at || p.bounced_at || p.unsubscribed_at
                  || p.cold_followup_at || p.cold_outreach_at || p.updated_at;
                return (
                  <tr key={p.id} className="hover:bg-surface-2/40">
                    <td className="px-4 py-2.5">
                      <Link to={`/admin/prospects/${p.id}`} className="text-text hover:text-accent whitespace-nowrap">
                        {p.business_name}
                      </Link>
                      {p.location && <div className="text-xs text-text-muted whitespace-nowrap">{p.location}</div>}
                    </td>
                    <td className="px-4 py-2.5 text-text-muted whitespace-nowrap">{p.industry || '—'}</td>
                    <td className="px-4 py-2.5 text-text-muted whitespace-nowrap">{p.contact_email || '—'}</td>
                    <td className="px-4 py-2.5"><StatusPill tone={stage}>{stage}</StatusPill></td>
                    <td className="px-4 py-2.5 text-text-muted whitespace-nowrap">{fmtRelative(lastTs)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollTable>
      )}

      {data && data.total > PAGE && (
        <div className="flex items-center justify-between mt-4 text-sm text-text-muted">
          <div>Page {page} of {totalPages} · {data.total} total</div>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
              disabled={offset === 0}
              className="px-3 py-1.5 bg-surface border border-border rounded-lg disabled:opacity-50"
            >Previous</button>
            <button
              onClick={() => setOffset(offset + PAGE)}
              disabled={offset + PAGE >= data.total}
              className="px-3 py-1.5 bg-surface border border-border rounded-lg disabled:opacity-50"
            >Next</button>
          </div>
        </div>
      )}
    </>
  );
}
